import React, { useEffect, useState } from "react";
import Input from "../../extra/Input";
import {
  getSettingApi,
  editSetting,
  switchApi,
  getWithdrawalApi,
  checkItemPurchaseCode,
} from "../../store/setting/setting.action";
import { Skeleton, Switch } from "@mui/material";
import styled from "@emotion/styled";
import { connect, useDispatch, useSelector } from "react-redux";

import { permissionError } from "../../../util/Alert";
import { setToast } from "../../../util/toast";
import PaymentRestrictionsDialog from "../../dialogue/PaymentRestrictionsDialog";
import {
  cashfreeContent,
  flutterWaveContent,
  googlePlayContent,
  nowPaymentContent,
  paypalContent,
  paystackContent,
  razorpayContent,
  stripeContent,
} from "../../extra/infoContent";
import InfoTooltip from "../../extra/InfoTooltip";

const CompactSwitch = styled(Switch)(() => ({
  width: 40,
  height: 22,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 2,
    margin: 0,
    transitionDuration: "200ms",
    "&.Mui-checked": {
      transform: "translateX(18px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#ff5e75",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: "#f3f4f6",
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.4,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 18,
    height: 18,
    backgroundColor: "#fff",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.2)",
  },
  "& .MuiSwitch-track": {
    borderRadius: 22,
    backgroundColor: "#d1d5db",
    opacity: 1,
  },
}));

const SETUP_GUIDE_URLS = {
  razorpay: "https://dashboard.razorpay.com/",
  stripe: "https://dashboard.stripe.com/apikeys",
  paystack: "https://dashboard.paystack.com/",
  cashfree: "https://merchant.cashfree.com/",
  paypal: "https://developer.paypal.com/",
  flutterwave: "https://dashboard.flutterwave.com/",
  googlePlay: "https://developer.android.com/google/play/billing",
  nowPayments: "https://account.nowpayments.io/",
};

const PlatformToggle = ({
  label,
  iconClass,
  checked,
  disabled,
  loading,
  onChange,
}) => (
  <div className="pg-platform-chip">
    <span className="pg-platform-chip-meta">
      <i className={iconClass} aria-hidden />
      <span>{label}</span>
    </span>
    {loading ? (
      <Skeleton
        variant="rectangular"
        width={40}
        height={22}
        style={{ borderRadius: 22 }}
      />
    ) : (
      <CompactSwitch
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={onChange}
        size="small"
        inputProps={{ "aria-label": `${label} switch` }}
      />
    )}
  </div>
);

const PaymentGatewayCard = ({
  logoClass,
  logoText,
  logoSrc,
  name,
  description,
  platforms,
  setupGuideUrl,
  setupTitle,
  setupContent,
  isLoading,
  isSwitchDisabled,
  children,
  extraRow,
  footerNote = "Credentials required to enable payments.",
  showCredentials = true,
}) => (
  <article
    className={`pg-card${!showCredentials ? " pg-card--compact" : ""}`}
  >
    <div className="pg-card-header">
      <div className="pg-card-brand">
        <div
          className={`pg-card-logo ${logoClass}${logoSrc ? " pg-card-logo--image" : ""}`}
          aria-hidden
        >
          {logoSrc ? (
            <img src={logoSrc} alt="" />
          ) : (
            logoText
          )}
        </div>
        <div className="pg-card-brand-text">
          <h3 className="pg-card-title">{name}</h3>
          <p className="pg-card-desc">{description}</p>
        </div>
      </div>
      {setupContent && (
        <div className="pg-card-header-info">
          <InfoTooltip title={setupTitle || name} content={setupContent} />
        </div>
      )}
    </div>

    <div className="pg-card-section">
      <p className="pg-section-label">Available on</p>
      <div className="pg-platform-row">
        {platforms.map((platform) => (
          <PlatformToggle
            key={platform.key}
            label={platform.label}
            iconClass={platform.iconClass}
            checked={platform.checked}
            disabled={isSwitchDisabled}
            loading={isLoading}
            onChange={platform.onChange}
          />
        ))}
      </div>
    </div>

    {extraRow}

    {showCredentials && (
      <div className="pg-card-section pg-card-section--credentials">
        <div className="pg-credentials-header">
          <p className="pg-section-label mb-0">API credentials</p>
          <div className="pg-credentials-actions">
            {setupGuideUrl && (
              <a
                className="pg-setup-guide"
                href={setupGuideUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Setup Guide
                <i className="bi bi-box-arrow-up-right" aria-hidden />
              </a>
            )}
          </div>
        </div>
        <div className="pg-credentials-grid">{children}</div>
        {footerNote && <p className="pg-credentials-note">{footerNote}</p>}
      </div>
    )}
  </article>
);

function PaymentSetting(props) {
  const { settingData, withdrawData } = useSelector((state) => state.setting);
  const { isLoading } = useSelector((state) => state.dialogue);
  const isDemo = sessionStorage.getItem("demo");
  const hasPermission = useSelector((state) => state.admin.admin.flag);
  const dispatch = useDispatch();
  const [data, setData] = useState();
  const [originalData, setOriginalData] = useState({});
  const [stripePublishableKey, setStripePublishableKey] = useState();
  const [stripeSecretKey, setStripeSecretKey] = useState();
  const [razorPayId, setRazorPayId] = useState();
  const [razorSecretKey, setRazorSecretKey] = useState();
  const [stripeSwitch, setStripeSwitch] = useState();
  const [stripeIosSwitch, setStripeIosSwitch] = useState();
  const [stripeWebEnabled, setStripeWebEnabled] = useState();
  const [razorPaySwitch, setRazorPaySwitch] = useState();
  const [razorPayIosSwitch, setRazorPayIosSwitch] = useState();
  const [razorpayWebEnabled, setRazorpayWebEnabled] = useState();
  const [bankTransfer, setBankTransfer] = useState();
  const [customMethod, setCustomMethod] = useState();
  const [payPal, setPayPal] = useState();
  const [skrill, setSkrill] = useState();
  const [googlePlaySwitch, setGooglePlaySwitch] = useState();
  const [googlePlayIosSwitch, setGooglePlayIosSwitch] = useState();
  const [googlePayWebEnabled, setGooglePayWebEnabled] = useState();
  const [minWithdrawalRequest, setMinWithdrawalRequest] = useState();
  const [customMethodText, setCustomMethodText] = useState();
  const [withdrawDataName, setWithdrawDataName] = useState([]);

  const [flutterWaveId, setFlutterWaveId] = useState();
  const [flutterWaveSwitch, setFlutterWaveSwitch] = useState();
  const [flutterWaveIosSwitch, setFlutterWaveIosSwitch] = useState();
  const [flutterwaveWebEnabled, setFlutterwaveWebEnabled] = useState();

  const [paystackAndroidEnabled, setPaystackAndroidEnabled] = useState();
  const [paystackIosEnabled, setPaystackIosEnabled] = useState();
  const [paystackWebEnabled, setPaystackWebEnabled] = useState();

  const [cashfreeAndroidEnabled, setCashfreeAndroidEnabled] = useState();
  const [cashfreeIosEnabled, setCashfreeIosEnabled] = useState();
  const [cashfreeWebEnabled, setCashfreeWebEnabled] = useState();

  const [paypalAndroidEnabled, setPaypalAndroidEnabled] = useState();
  const [paypalIosEnabled, setPaypalIosEnabled] = useState();
  const [paypalWebEnabled, setPaypalWebEnabled] = useState();

  const [nowPaymentAndroidEnabled, setNowPaymentAndroidEnabled] = useState();
  const [nowPaymentIosEnabled, setNowPaymentIosEnabled] = useState();
  const [nowPaymentWebEnabled, setNowPaymentWebEnabled] = useState();
  const [nowPaymentApiKey, setNowPaymentApiKey] = useState();
  const [nowPaymentSandboxKey, setNowPaymentSandboxKey] = useState();
  const [nowPaymentIpnSecret, setNowPaymentIpnSecret] = useState();
  const [nowPaymentIsTest, setNowPaymentIsTest] = useState();
  const [nowPaymentPayCurrency, setNowPaymentPayCurrency] = useState();

  const [paystackPublicKey, setPaystackPublicKey] = useState();
  const [paystackSecretKey, setPaystackSecretKey] = useState();
  const [cashfreeClientId, setCashfreeClientId] = useState();
  const [cashfreeClientSecret, setCashfreeClientSecret] = useState();
  const [paypalClientId, setPaypalClientId] = useState();
  const [paypalSecretKey, setPaypalSecretKey] = useState();

  const [error, setError] = useState({
    razorPayId: "",
    razorSecretKey: "",
    stripePublishableKey: "",
    stripeSecretKey: "",
    minWithdrawalRequest: "",
    customMethodText: "",
    paystackPublicKey: "",
    paystackSecretKey: "",
    cashfreeClientId: "",
    cashfreeClientSecret: "",
    paypalClientId: "",
    paypalSecretKey: "",
    flutterWaveId: "",
    apiKey: "",
    sandboxKey: "",
    ipnSecret: "",
    payCurrency: "",
  });

  const [isCheckedOnce, setIsCheckedOnce] = useState(false);
  const [isPaymentAllowed, setIsPaymentAllowed] = useState(null);
  const [paymentRestrictionOpen, setPaymentRestrictionOpen] = useState(false);
  const [paymentCheckLoading, setPaymentCheckLoading] = useState(false);

  useEffect(() => {
    if (isDemo === "demo@admin.com") return;
    dispatch(getSettingApi());
  }, [dispatch]);

  useEffect(() => {
    setData(settingData);
  }, [settingData]);

  useEffect(() => {
    const filterData = withdrawData?.filter((item) => item?.isEnabled === true);

    const getNameWithdrawData = filterData?.map(
      (item) => " " + item?.name + " "
    );
    setWithdrawDataName(getNameWithdrawData);
  }, [withdrawData]);

  useEffect(() => {
    setRazorPayId(settingData?.razorPayId);
    setRazorSecretKey(settingData?.razorSecretKey);
    setRazorPaySwitch(settingData?.razorPaySwitch);
    setRazorPayIosSwitch(settingData?.razorpayIosEnabled);
    setRazorpayWebEnabled(settingData?.razorpayWebEnabled);
    setStripeSwitch(settingData?.stripeSwitch);
    setStripeIosSwitch(settingData?.stripeIosEnabled);
    setStripeWebEnabled(settingData?.stripeWebEnabled);
    setStripePublishableKey(settingData?.stripePublishableKey);
    setStripeSecretKey(settingData?.stripeSecretKey);
    setBankTransfer(settingData?.bankTransfer);
    setCustomMethod(settingData?.customMethod);
    setPayPal(settingData?.payPal);
    setSkrill(settingData?.skrill);
    setGooglePlaySwitch(settingData?.googlePlaySwitch);
    setGooglePlayIosSwitch(settingData?.googlePayIosEnabled);
    setGooglePayWebEnabled(settingData?.googlePayWebEnabled);
    setMinWithdrawalRequest(settingData?.minWithdrawalRequest);
    setFlutterWaveId(settingData?.flutterWaveId);
    setFlutterWaveSwitch(settingData?.flutterWaveSwitch);
    setFlutterWaveIosSwitch(settingData?.flutterwaveIosEnabled);
    setFlutterwaveWebEnabled(settingData?.flutterwaveWebEnabled);
    setPaystackAndroidEnabled(settingData?.paystackAndroidEnabled);
    setPaystackIosEnabled(settingData?.paystackIosEnabled);
    setPaystackWebEnabled(settingData?.paystackWebEnabled);
    setCashfreeAndroidEnabled(settingData?.cashfreeAndroidEnabled);
    setCashfreeIosEnabled(settingData?.cashfreeIosEnabled);
    setCashfreeWebEnabled(settingData?.cashfreeWebEnabled);
    setPaypalAndroidEnabled(settingData?.paypalAndroidEnabled);
    setPaypalIosEnabled(settingData?.paypalIosEnabled);
    setPaypalWebEnabled(settingData?.paypalWebEnabled);
    setNowPaymentAndroidEnabled(settingData?.nowPaymentAndroidEnabled);
    setNowPaymentIosEnabled(settingData?.nowPaymentIosEnabled);
    setNowPaymentWebEnabled(settingData?.nowPaymentWebEnabled);
    setNowPaymentApiKey(settingData?.apiKey);
    setNowPaymentSandboxKey(settingData?.sandboxKey);
    setNowPaymentIpnSecret(settingData?.ipnSecret);
    setNowPaymentIsTest(settingData?.isTest);
    setNowPaymentPayCurrency(settingData?.payCurrency);
    setPaystackPublicKey(settingData?.paystackPublicKey);
    setPaystackSecretKey(settingData?.paystackSecretKey);
    setCashfreeClientId(settingData?.cashfreeClientId);
    setCashfreeClientSecret(settingData?.cashfreeClientSecret);
    setPaypalClientId(settingData?.paypalClientId);
    setPaypalSecretKey(settingData?.paypalSecretKey);

    setOriginalData({
      razorPayId: settingData?.razorPayId,
      razorSecretKey: settingData?.razorSecretKey,
      stripePublishableKey: settingData?.stripePublishableKey,
      stripeSecretKey: settingData?.stripeSecretKey,
      minWithdrawalRequest: settingData?.minWithdrawalRequest,
      flutterWaveId: settingData?.flutterWaveId,
      paystackPublicKey: settingData?.paystackPublicKey,
      paystackSecretKey: settingData?.paystackSecretKey,
      cashfreeClientId: settingData?.cashfreeClientId,
      cashfreeClientSecret: settingData?.cashfreeClientSecret,
      paypalClientId: settingData?.paypalClientId,
      paypalSecretKey: settingData?.paypalSecretKey,
      apiKey: settingData?.apiKey,
      sandboxKey: settingData?.sandboxKey,
      ipnSecret: settingData?.ipnSecret,
      payCurrency: settingData?.payCurrency,
    });
  }, [settingData]);

  const handleSubmit = () => {
    if (!hasPermission) return permissionError();
    const minWithdrawalRequestValue = parseInt(minWithdrawalRequest);

    if (
      !razorPayId ||
      !razorSecretKey ||
      !stripePublishableKey ||
      !stripeSecretKey ||
      minWithdrawalRequest === "" ||
      (customMethod && customMethodText) ||
      minWithdrawalRequestValue <= 0
    ) {
      let error = {};
      if (!razorPayId) error.razorPayId = "Razor Pay Id Is Required !";
      if (!razorSecretKey)
        error.razorSecretKey = "Razor Secret Key Is Required !";
      if (!stripePublishableKey)
        error.stripePublishableKey = "Stripe Publishable Key Is Required !";
      if (!stripeSecretKey)
        error.stripeSecretKey = "Stripe Secret Key Is Required !";
      if (minWithdrawalRequest === "")
        error.minWithdrawalRequest = "Withdrawal Amount Request Is Required !";
      if (customMethod && customMethodText)
        error.customMethodText = "Custom Method Is Required !";
      if (minWithdrawalRequestValue <= 0)
        error.minWithdrawalRequest = "Withdrawal Amount Invalid !";
      return setError({ ...error });
    } else {
      const currentData = {
        razorPayId,
        razorSecretKey,
        stripePublishableKey,
        stripeSecretKey,
        minWithdrawalRequest: parseInt(minWithdrawalRequest),
        flutterWaveId,
        paystackPublicKey,
        paystackSecretKey,
        cashfreeClientId,
        cashfreeClientSecret,
        paypalClientId,
        paypalSecretKey,
        apiKey: nowPaymentApiKey,
        sandboxKey: nowPaymentSandboxKey,
        ipnSecret: nowPaymentIpnSecret,
        payCurrency: nowPaymentPayCurrency,
      };

      let updatedFields = {};
      for (let key in currentData) {
        if (currentData[key] !== originalData[key]) {
          updatedFields[key] = currentData[key];
        }
      }

      let settingDataSubmit = {
        settingId: data?._id,
        ...updatedFields,
      };
      props.editSetting(data?._id, settingDataSubmit);
    }
  };

  useEffect(() => {
    if (props.submitTrigger > 0) {
      handleSubmit();
    }
  }, [props.submitTrigger]);

  const ensurePaymentToggleAllowed = async () => {
    setIsCheckedOnce(true);
    setIsPaymentAllowed(true);
    return true;

    // ------------------------------

    if (isCheckedOnce) {
      if (isPaymentAllowed === false) {
        setPaymentRestrictionOpen(true);
        return false;
      }
      return true;
    }

    setPaymentCheckLoading(true);

    try {
      const payload = await dispatch(checkItemPurchaseCode());

      setIsCheckedOnce(true);

      const allowed =
        payload?.status === true && payload?.allowPaymentSettings === true;

      setIsPaymentAllowed(allowed);

      if (!allowed) {
        setPaymentRestrictionOpen(true);
        return false;
      }

      return true;
    } catch (e) {
      setToast(
        "error",
        e?.message || "Could not verify payment license. Please try again."
      );
      return false;
    } finally {
      setPaymentCheckLoading(false);
    }
  };

  const getCurrentSwitchValue = (method) => {
    switch (method) {
      case "bankTransfer":
        return bankTransfer;
      case "payPal":
        return payPal;
      case "skrill":
        return skrill;
      case "customMethod":
        return customMethod;
      case "razorPay":
        return razorPaySwitch;
      case "razorpayIosEnabled":
        return razorPayIosSwitch;
      case "razorpayWebEnabled":
        return razorpayWebEnabled;
      case "googlePlaySwitch":
        return googlePlaySwitch;
      case "googlePayIosEnabled":
        return googlePlayIosSwitch;
      case "googlePayWebEnabled":
        return googlePayWebEnabled;
      case "stripe":
        return stripeSwitch;
      case "stripeIosEnabled":
        return stripeIosSwitch;
      case "stripeWebEnabled":
        return stripeWebEnabled;
      case "flutterWave":
        return flutterWaveSwitch;
      case "flutterwaveIosEnabled":
        return flutterWaveIosSwitch;
      case "flutterwaveWebEnabled":
        return flutterwaveWebEnabled;
      case "paystackAndroidEnabled":
        return paystackAndroidEnabled;
      case "paystackIosEnabled":
        return paystackIosEnabled;
      case "paystackWebEnabled":
        return paystackWebEnabled;
      case "cashfreeAndroidEnabled":
        return cashfreeAndroidEnabled;
      case "cashfreeIosEnabled":
        return cashfreeIosEnabled;
      case "cashfreeWebEnabled":
        return cashfreeWebEnabled;
      case "paypalAndroidEnabled":
        return paypalAndroidEnabled;
      case "paypalIosEnabled":
        return paypalIosEnabled;
      case "paypalWebEnabled":
        return paypalWebEnabled;
      case "nowPaymentAndroidEnabled":
        return nowPaymentAndroidEnabled;
      case "nowPaymentIosEnabled":
        return nowPaymentIosEnabled;
      case "nowPaymentWebEnabled":
        return nowPaymentWebEnabled;
      case "isTest":
        return nowPaymentIsTest;
      default:
        return undefined;
    }
  };

  const isSwitchDisabled = !hasPermission || paymentCheckLoading;

  const handleChange = async (method) => {
    if (!hasPermission) return permissionError();
    const canToggle = await ensurePaymentToggleAllowed();
    if (!canToggle) return;

    const currentValue = getCurrentSwitchValue(method);
    if (currentValue === undefined) return;

    try {
      await props.switchApi(data?._id, method, currentValue);
    } catch (error) {
      console.error("Error updating payment methods:", error);
    }
  };

  const clearFieldError = (field) =>
    setError((prev) => ({
      ...prev,
      [field]: "",
    }));

  const setRequiredError = (field, message) =>
    setError((prev) => ({
      ...prev,
      [field]: message,
    }));

  return (
    <div className="payment-setting p-0 mainSetting">
      <PaymentRestrictionsDialog
        open={paymentRestrictionOpen}
        onClose={() => setPaymentRestrictionOpen(false)}
      />

      <div className="pg-page">
        <div className="pg-page-header">
          <div>
            <h2 className="pg-page-title">Payment Gateways</h2>
            <p className="pg-page-subtitle">
              Configure credentials and choose where each gateway is available
              in your app.
            </p>
          </div>
        </div>

        <div className="pg-grid">
          <PaymentGatewayCard
            logoClass="pg-logo-razorpay"
            logoSrc={`${process.env.PUBLIC_URL}/images/razorpay.png`}
            name="Razor Pay"
            description="Payment gateway for India"
            platforms={[
              {
                key: "android",
                label: "Android",
                iconClass: "bi bi-android2",
                checked: razorPaySwitch,
                onChange: () => handleChange("razorPay"),
              },
              {
                key: "ios",
                label: "iOS",
                iconClass: "bi bi-apple",
                checked: razorPayIosSwitch,
                onChange: () => handleChange("razorpayIosEnabled"),
              },
              {
                key: "web",
                label: "Web",
                iconClass: "bi bi-globe",
                checked: razorpayWebEnabled,
                onChange: () => handleChange("razorpayWebEnabled"),
              },
            ]}
            setupGuideUrl={SETUP_GUIDE_URLS.razorpay}
            setupTitle="Razorpay Setting"
            setupContent={razorpayContent}
            isLoading={isLoading}
            isSwitchDisabled={isSwitchDisabled}
          >
            <Input
              label={"Razor Pay Id"}
              name={"razorPayId"}
              type={"text"}
              loading={isLoading}
              value={razorPayId}
              errorMessage={error.razorPayId && error.razorPayId}
              placeholder={
                isDemo === "demo@admin.com"
                  ? "rzpid_1234567890abcdef"
                  : "Enter Details..."
              }
              onChange={(e) => {
                setRazorPayId(e.target.value);
                if (!e.target.value) {
                  setRequiredError("razorPayId", "RazorPayId Is Required");
                } else {
                  clearFieldError("razorPayId");
                }
              }}
            />
            <Input
              label={"Razor Secret Key"}
              name={"razorSecretKey"}
              type={"password"}
              loading={isLoading}
              value={razorSecretKey}
              errorMessage={error.razorSecretKey && error.razorSecretKey}
              placeholder={
                isDemo === "demo@admin.com"
                  ? "rzpsk_test_secretkey987654321"
                  : "Enter Details..."
              }
              onChange={(e) => {
                setRazorSecretKey(e.target.value);
                if (!e.target.value) {
                  setRequiredError(
                    "razorSecretKey",
                    "RazorSecretKey Is Required"
                  );
                } else {
                  clearFieldError("razorSecretKey");
                }
              }}
            />
          </PaymentGatewayCard>

          <PaymentGatewayCard
            logoClass="pg-logo-stripe"
            logoSrc={`${process.env.PUBLIC_URL}/images/stripe.png`}
            name="Stripe"
            description="Global card payments"
            platforms={[
              {
                key: "android",
                label: "Android",
                iconClass: "bi bi-android2",
                checked: stripeSwitch,
                onChange: () => handleChange("stripe"),
              },
              {
                key: "ios",
                label: "iOS",
                iconClass: "bi bi-apple",
                checked: stripeIosSwitch,
                onChange: () => handleChange("stripeIosEnabled"),
              },
              {
                key: "web",
                label: "Web",
                iconClass: "bi bi-globe",
                checked: stripeWebEnabled,
                onChange: () => handleChange("stripeWebEnabled"),
              },
            ]}
            setupGuideUrl={SETUP_GUIDE_URLS.stripe}
            setupTitle="Stripe Payment Setting"
            setupContent={stripeContent}
            isLoading={isLoading}
            isSwitchDisabled={isSwitchDisabled}
          >
            <Input
              label={"Publishable Key"}
              name={"stripePublishableKey"}
              type={"text"}
              loading={isLoading}
              value={
                isDemo === "demo@admin.com"
                  ? "pk_test_51H8a3D2Yx7kfZT1x1pQ"
                  : stripePublishableKey
              }
              errorMessage={
                error.stripePublishableKey && error.stripePublishableKey
              }
              placeholder={"Enter Details..."}
              onChange={(e) => {
                setStripePublishableKey(e.target.value);
                if (!e.target.value) {
                  setRequiredError(
                    "stripePublishableKey",
                    "StripePublishableKey Is Required"
                  );
                } else {
                  clearFieldError("stripePublishableKey");
                }
              }}
            />
            <Input
              label={"Secret Key"}
              name={"stripeSecretKey"}
              type={"password"}
              loading={isLoading}
              value={stripeSecretKey}
              errorMessage={error.stripeSecretKey && error.stripeSecretKey}
              placeholder={
                isDemo === "demo@admin.com"
                  ? "sk_test_4eC39HqLyjWDarjtT1zdp7dc"
                  : "Enter Details..."
              }
              onChange={(e) => {
                setStripeSecretKey(e.target.value);
                if (!e.target.value) {
                  setRequiredError(
                    "stripeSecretKey",
                    "StripeSecretKey Is Required"
                  );
                } else {
                  clearFieldError("stripeSecretKey");
                }
              }}
            />
          </PaymentGatewayCard>

          <PaymentGatewayCard
            logoClass="pg-logo-paystack"
            logoSrc={`${process.env.PUBLIC_URL}/images/paystack.png`}
            name="Paystack"
            description="Payments for Africa"
            platforms={[
              {
                key: "android",
                label: "Android",
                iconClass: "bi bi-android2",
                checked: paystackAndroidEnabled,
                onChange: () => handleChange("paystackAndroidEnabled"),
              },
              {
                key: "ios",
                label: "iOS",
                iconClass: "bi bi-apple",
                checked: paystackIosEnabled,
                onChange: () => handleChange("paystackIosEnabled"),
              },
              {
                key: "web",
                label: "Web",
                iconClass: "bi bi-globe",
                checked: paystackWebEnabled,
                onChange: () => handleChange("paystackWebEnabled"),
              },
            ]}
            setupGuideUrl={SETUP_GUIDE_URLS.paystack}
            setupTitle="Paystack Setting"
            setupContent={paystackContent}
            isLoading={isLoading}
            isSwitchDisabled={isSwitchDisabled}
          >
            <Input
              label={"Public Key"}
              name={"paystackPublicKey"}
              type={"text"}
              loading={isLoading}
              value={paystackPublicKey}
              errorMessage={error.paystackPublicKey && error.paystackPublicKey}
              placeholder={"Enter Paystack Public Key"}
              onChange={(e) => {
                setPaystackPublicKey(e.target.value);
                if (!e.target.value) {
                  setRequiredError(
                    "paystackPublicKey",
                    "Paystack Public Key Is Required"
                  );
                } else {
                  clearFieldError("paystackPublicKey");
                }
              }}
            />
            <Input
              label={"Secret Key"}
              name={"paystackSecretKey"}
              type={"password"}
              loading={isLoading}
              value={paystackSecretKey}
              errorMessage={error.paystackSecretKey && error.paystackSecretKey}
              placeholder={"Enter Paystack Secret Key"}
              onChange={(e) => {
                setPaystackSecretKey(e.target.value);
                if (!e.target.value) {
                  setRequiredError(
                    "paystackSecretKey",
                    "Paystack Secret Key Is Required"
                  );
                } else {
                  clearFieldError("paystackSecretKey");
                }
              }}
            />
          </PaymentGatewayCard>

          <PaymentGatewayCard
            logoClass="pg-logo-cashfree"
            logoSrc={`${process.env.PUBLIC_URL}/images/cashfree.png`}
            name="Cashfree"
            description="Indian payment solutions"
            platforms={[
              {
                key: "android",
                label: "Android",
                iconClass: "bi bi-android2",
                checked: cashfreeAndroidEnabled,
                onChange: () => handleChange("cashfreeAndroidEnabled"),
              },
              {
                key: "ios",
                label: "iOS",
                iconClass: "bi bi-apple",
                checked: cashfreeIosEnabled,
                onChange: () => handleChange("cashfreeIosEnabled"),
              },
              {
                key: "web",
                label: "Web",
                iconClass: "bi bi-globe",
                checked: cashfreeWebEnabled,
                onChange: () => handleChange("cashfreeWebEnabled"),
              },
            ]}
            setupGuideUrl={SETUP_GUIDE_URLS.cashfree}
            setupTitle="Cashfree Setting"
            setupContent={cashfreeContent}
            isLoading={isLoading}
            isSwitchDisabled={isSwitchDisabled}
          >
            <Input
              label={"Client Id"}
              name={"cashfreeClientId"}
              type={"text"}
              loading={isLoading}
              value={cashfreeClientId}
              errorMessage={error.cashfreeClientId && error.cashfreeClientId}
              placeholder={
                isDemo === "demo@admin.com"
                  ? "FLWSECK_TEST-5f4a5b0a9e2b6b3b3d4b5b6b7b8b9b0b"
                  : "Enter Cashfree Client Id"
              }
              onChange={(e) => {
                setCashfreeClientId(e.target.value);
                if (!e.target.value) {
                  setRequiredError(
                    "cashfreeClientId",
                    "Cashfree Client Id Is Required"
                  );
                } else {
                  clearFieldError("cashfreeClientId");
                }
              }}
            />
            <Input
              label={"Client Secret"}
              name={"cashfreeClientSecret"}
              type={"password"}
              loading={isLoading}
              value={cashfreeClientSecret}
              errorMessage={
                error.cashfreeClientSecret && error.cashfreeClientSecret
              }
              placeholder={"Enter Cashfree Client Secret"}
              onChange={(e) => {
                setCashfreeClientSecret(e.target.value);
                if (!e.target.value) {
                  setRequiredError(
                    "cashfreeClientSecret",
                    "Cashfree Client Secret Is Required"
                  );
                } else {
                  clearFieldError("cashfreeClientSecret");
                }
              }}
            />
          </PaymentGatewayCard>

          <PaymentGatewayCard
            logoClass="pg-logo-paypal"
            logoSrc={`${process.env.PUBLIC_URL}/images/paypal.png`}
            name="PayPal"
            description="Worldwide online payments"
            platforms={[
              {
                key: "android",
                label: "Android",
                iconClass: "bi bi-android2",
                checked: paypalAndroidEnabled,
                onChange: () => handleChange("paypalAndroidEnabled"),
              },
              {
                key: "ios",
                label: "iOS",
                iconClass: "bi bi-apple",
                checked: paypalIosEnabled,
                onChange: () => handleChange("paypalIosEnabled"),
              },
              {
                key: "web",
                label: "Web",
                iconClass: "bi bi-globe",
                checked: paypalWebEnabled,
                onChange: () => handleChange("paypalWebEnabled"),
              },
            ]}
            setupGuideUrl={SETUP_GUIDE_URLS.paypal}
            setupTitle="PayPal Setting"
            setupContent={paypalContent}
            isLoading={isLoading}
            isSwitchDisabled={isSwitchDisabled}
          >
            <Input
              label={"Client Id"}
              name={"payPalClientId"}
              type={"text"}
              loading={isLoading}
              value={paypalClientId}
              errorMessage={error.paypalClientId && error.paypalClientId}
              placeholder={"Enter PayPal Client Id"}
              onChange={(e) => {
                setPaypalClientId(e.target.value);
                if (!e.target.value) {
                  setRequiredError(
                    "paypalClientId",
                    "PayPal Client Id Is Required"
                  );
                } else {
                  clearFieldError("paypalClientId");
                }
              }}
            />
            <Input
              label={"Secret Key"}
              name={"paypalSecretKey"}
              type={"password"}
              loading={isLoading}
              value={paypalSecretKey}
              errorMessage={error.paypalSecretKey && error.paypalSecretKey}
              placeholder={"Enter PayPal Secret Key"}
              onChange={(e) => {
                setPaypalSecretKey(e.target.value);
                if (!e.target.value) {
                  setRequiredError(
                    "paypalSecretKey",
                    "PayPal Secret Key Is Required"
                  );
                } else {
                  clearFieldError("paypalSecretKey");
                }
              }}
            />
          </PaymentGatewayCard>

          <PaymentGatewayCard
            logoClass="pg-logo-flutterwave"
            logoSrc={`${process.env.PUBLIC_URL}/images/flutterwave.png`}
            name="Flutter Wave"
            description="African payment platform"
            platforms={[
              {
                key: "android",
                label: "Android",
                iconClass: "bi bi-android2",
                checked: flutterWaveSwitch,
                onChange: () => handleChange("flutterWave"),
              },
              {
                key: "ios",
                label: "iOS",
                iconClass: "bi bi-apple",
                checked: flutterWaveIosSwitch,
                onChange: () => handleChange("flutterwaveIosEnabled"),
              },
              {
                key: "web",
                label: "Web",
                iconClass: "bi bi-globe",
                checked: flutterwaveWebEnabled,
                onChange: () => handleChange("flutterwaveWebEnabled"),
              },
            ]}
            setupGuideUrl={SETUP_GUIDE_URLS.flutterwave}
            setupTitle="Flutter Wave Setting"
            setupContent={flutterWaveContent}
            isLoading={isLoading}
            isSwitchDisabled={isSwitchDisabled}
          >
            <Input
              label={"Flutter Wave Id"}
              name={"flutterWaveId"}
              type={"text"}
              loading={isLoading}
              value={flutterWaveId}
              errorMessage={error.flutterWaveId && error.flutterWaveId}
              placeholder={
                isDemo === "demo@admin.com"
                  ? "FLWSECK_TEST-5f4a5b0a9e2b6b3b3d4b5b6b7b8b9b0b"
                  : "Enter Details..."
              }
              onChange={(e) => {
                setFlutterWaveId(e.target.value);
                if (!e.target.value) {
                  setRequiredError("flutterWaveId", "flutterWaveId Is Required");
                } else {
                  clearFieldError("flutterWaveId");
                }
              }}
            />
          </PaymentGatewayCard>

          <PaymentGatewayCard
            logoClass="pg-logo-nowpayments"
            logoSrc={`${process.env.PUBLIC_URL}/images/nowpayment.png`}
            name="NowPayments"
            description="Crypto payment gateway"
            platforms={[
              {
                key: "android",
                label: "Android",
                iconClass: "bi bi-android2",
                checked: nowPaymentAndroidEnabled,
                onChange: () => handleChange("nowPaymentAndroidEnabled"),
              },
              {
                key: "ios",
                label: "iOS",
                iconClass: "bi bi-apple",
                checked: nowPaymentIosEnabled,
                onChange: () => handleChange("nowPaymentIosEnabled"),
              },
              {
                key: "web",
                label: "Web",
                iconClass: "bi bi-globe",
                checked: nowPaymentWebEnabled,
                onChange: () => handleChange("nowPaymentWebEnabled"),
              },
            ]}
            setupGuideUrl={SETUP_GUIDE_URLS.nowPayments}
            setupTitle="NowPayments Setting"
            setupContent={nowPaymentContent}
            isLoading={isLoading}
            isSwitchDisabled={isSwitchDisabled}
            extraRow={
              <div className="pg-card-section pg-test-mode-row">
                <div className="pg-platform-chip pg-platform-chip--wide">
                  <span className="pg-platform-chip-meta">
                    <i className="bi bi-flask" aria-hidden />
                    <span>Test Mode (sandbox / production)</span>
                  </span>
                  {isLoading ? (
                    <Skeleton
                      variant="rectangular"
                      width={40}
                      height={22}
                      style={{ borderRadius: 22 }}
                    />
                  ) : (
                    <CompactSwitch
                      checked={Boolean(nowPaymentIsTest)}
                      disabled={isSwitchDisabled}
                      onChange={() => handleChange("isTest")}
                      inputProps={{ "aria-label": "NowPayments test mode" }}
                    />
                  )}
                </div>
              </div>
            }
          >
            <Input
              label={"API Key"}
              name={"apiKey"}
              type={"password"}
              loading={isLoading}
              value={nowPaymentApiKey}
              errorMessage={error.apiKey && error.apiKey}
              placeholder={
                isDemo === "demo@admin.com"
                  ? "NOWPAYMENTS_API_KEY"
                  : "Enter API Key"
              }
              onChange={(e) => {
                setNowPaymentApiKey(e.target.value);
                if (!e.target.value) {
                  setRequiredError("apiKey", "API Key Is Required");
                } else {
                  clearFieldError("apiKey");
                }
              }}
            />
            <Input
              label={"Sandbox Key"}
              name={"sandboxKey"}
              type={"password"}
              loading={isLoading}
              value={nowPaymentSandboxKey}
              errorMessage={error.sandboxKey && error.sandboxKey}
              placeholder={
                isDemo === "demo@admin.com"
                  ? "NOWPAYMENTS_SANDBOX_KEY"
                  : "Enter Sandbox Key"
              }
              onChange={(e) => {
                setNowPaymentSandboxKey(e.target.value);
                if (!e.target.value) {
                  setRequiredError("sandboxKey", "Sandbox Key Is Required");
                } else {
                  clearFieldError("sandboxKey");
                }
              }}
            />
            <Input
              label={"IPN Secret"}
              name={"ipnSecret"}
              type={"password"}
              loading={isLoading}
              value={nowPaymentIpnSecret}
              errorMessage={error.ipnSecret && error.ipnSecret}
              placeholder={
                isDemo === "demo@admin.com"
                  ? "NOWPAYMENTS_IPN_SECRET"
                  : "Enter IPN Secret"
              }
              onChange={(e) => {
                setNowPaymentIpnSecret(e.target.value);
                clearFieldError("ipnSecret");
              }}
            />
            <Input
              label={"Pay Currency"}
              name={"payCurrency"}
              type={"text"}
              loading={isLoading}
              value={nowPaymentPayCurrency}
              errorMessage={error.payCurrency && error.payCurrency}
              placeholder={
                isDemo === "demo@admin.com" ? "usdttrc20" : "Enter Pay Currency"
              }
              onChange={(e) => {
                setNowPaymentPayCurrency(e.target.value);
                clearFieldError("payCurrency");
              }}
            />
          </PaymentGatewayCard>

          <PaymentGatewayCard
            logoClass="pg-logo-googleplay"
            logoSrc={`${process.env.PUBLIC_URL}/images/googlepay.png`}
            name="Google Play"
            description="In-app purchase for Android"
            platforms={[
              {
                key: "android",
                label: "Android",
                iconClass: "bi bi-android2",
                checked: googlePlaySwitch,
                onChange: () => handleChange("googlePlaySwitch"),
              },
              {
                key: "ios",
                label: "iOS",
                iconClass: "bi bi-apple",
                checked: googlePlayIosSwitch,
                onChange: () => handleChange("googlePayIosEnabled"),
              },
              {
                key: "web",
                label: "Web",
                iconClass: "bi bi-globe",
                checked: googlePayWebEnabled,
                onChange: () => handleChange("googlePayWebEnabled"),
              },
            ]}
            setupGuideUrl={SETUP_GUIDE_URLS.googlePlay}
            setupTitle="GooglePlay Setting"
            setupContent={googlePlayContent}
            isLoading={isLoading}
            isSwitchDisabled={isSwitchDisabled}
            showCredentials={false}
          />
        </div>

        {/* <div className="pg-footer-banner" role="note">
          <i className="bi bi-info-circle-fill" aria-hidden />
          <p>
            Review all settings before submitting. Make sure your API
            credentials are correct and enabled for the selected platforms.
          </p>
        </div> */}
      </div>
    </div>
  );
}

export default connect(null, {
  getSettingApi,
  editSetting,
  switchApi,
  getWithdrawalApi,
})(PaymentSetting);
